// Phase 1A Step 23 — usePhantomCycleHistory hook.
//
// Reads the authenticated user's terminal Phantom slip_cycles rows
// (won / busted / expired) for the temporary dashboard history panel.
// Companion to usePhantomCycle which reads the most recent cycle in any
// visible status. This hook is read-only.
//
// Trust contract:
//   - RLS limits SELECT to the caller's own slip_cycles rows. The hook
//     never queries fixture_cache, never calls RPCs, never writes.
//   - Every value rendered comes from columns the resolve RPC stored:
//     status, mode, stake_amount, selections, result, result_type,
//     result_source, created_at. No fake metrics anywhere.
//
// This panel is acceptable Phase 1A scaffolding only. Slip-history is
// destined for The Brief (Mode 3) per docs/ui-ux-source-of-truth.md
// §E.4 once the mode-architecture refactor lands. Until then, this hook
// + panel sit inside the temporary <AppShell> dashboard tree.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type { PhantomCycleRow } from "./usePhantomCycle.ts";

// Phantom terminal statuses. Mirrors slip_cycles.status_matches_mode
// CHECK constraint (Migration 0004) for mode='phantom':
//   phantom_won | phantom_busted | expired
// 'expired' is included even though Phase 1A does not write it yet —
// once an expiry path is added, those rows surface here automatically.
const TERMINAL_STATUSES: readonly string[] = ["phantom_won", "phantom_busted", "expired"];

const HISTORY_LIMIT = 10;

export interface PhantomCycleHistoryState {
  cycles: PhantomCycleRow[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePhantomCycleHistory(): PhantomCycleHistoryState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [cycles, setCycles] = useState<PhantomCycleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setCycles([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from("slip_cycles")
      .select("*")
      .eq("user_id", user.id)
      .eq("mode", "phantom")
      .in("status", TERMINAL_STATUSES)
      .order("created_at", { ascending: false })
      .limit(HISTORY_LIMIT);
    if (fetchError) {
      setCycles([]);
      setError(fetchError.message);
    } else {
      setCycles((data ?? []) as PhantomCycleRow[]);
    }
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<PhantomCycleHistoryState>(
    () => ({ cycles, loading, error, refetch }),
    [cycles, loading, error, refetch],
  );
}
